import { IContactLink } from 'src/services/connections/connections.types';
import type { AddContactProps, RemoveContactProps, UpdateContactProps, GetOrgContactsQuery, IContactCompanyLink, IContactListItem, AddContactLinkProps, ContactDocument, ICreateContactDocumentDto, BulkContactImportProps } from 'src/services/contacts/contacts.types';
import { ContactLinkEntityType, Contact } from 'src/services/contacts/contacts.types';

import { Activity } from 'src/services/activities/activities.types';

import { rootApi } from '../rootApi';

import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';

export const contactsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganizationContacts: builder.query<PaginatedResponse<Contact>, GetOrgContactsQuery>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/contacts`,
        params: {
          search_query: data.search_query,
          contact_type: data.contact_type,
          ...data.page,
          ...(data.sort_config || {}),
        },
      }),
      providesTags: () => ['OrgContacts'],
    }),

    // Lean list read path for the shared contacts list (JT1-1527/1528). Separate from
    // getOrganizationContacts (other screens depend on its shape); returns flat IContactListItem.
    getOrganizationContactsLean: builder.query<PaginatedResponse<IContactListItem>, GetOrgContactsQuery>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/contacts/list`,
        params: {
          search_query: data.search_query,
          contact_type: data.contact_type,
          ...(data.column_filters && Object.keys(data.column_filters).length
            ? { column_filters: JSON.stringify(data.column_filters) }
            : {}),
          ...data.page,
          ...(data.sort_config || {}),
        },
      }),
      providesTags: () => ['OrgContacts'],
    }),

    getContactById: builder.query<Contact, string>({
      query: (contactId) => ({
        url: `/organizations/contacts/${contactId}`,
      }),
      providesTags: (result, error, id) => [{ type: 'OrgContacts', id }],
    }),

    addContact: builder.mutation<Contact, AddContactProps>({
      query: (data) => ({
        url: `/organizations/${data.org_id}/contacts`,
        method: HTTPRequestType.POST,
        data: {
          account: data.account,
          manual_contact: data.manual_contact,
          source: data.source,
        },
      }),
      invalidatesTags: ['OrgContacts'],
    }),

    updateContact: builder.mutation<Contact, UpdateContactProps>({
      query: (data) => ({
        url: `/organizations/contacts/${data.contact_id}`,
        method: HTTPRequestType.PUT,
        data: {
          company_name: data.company_name,
          title: data.title,
          position_title: data.position_title,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone,
          special_information: data.special_information,
          linked_candidate_profile_id: data.linked_candidate_profile_id,
          links: data.links,
          custom_fields: data.custom_fields,
        },
      }),
      invalidatesTags: (result, error, { contact_id }) => [{ type: 'OrgContacts', id: contact_id }],
    }),

    bulkContactImport: builder.mutation<Array<{ email: string; status: 'success' | 'failed'; message?: string }>, { data: BulkContactImportProps }>({
      query: ({ data }) => ({
        url: `/organizations/contacts/csv-import`,
        method: HTTPRequestType.POST,
        data: {
          organization_id: data.organization_id,
          data: data.contacts,
        }
      }),
    }),

    updateContactAccountOwner: builder.mutation<APIResponse, { contact_id: string, data: { account_id: string | null } }>({
      query: ({ contact_id, data }) => ({
        url: `/organizations/contacts/${contact_id}/account-owner`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'OrgContacts', id: args.contact_id }],
    }),

    updateContactCandidateLink: builder.mutation<APIResponse, { contact_id: string, data: { candidate_id: string | null } }>({
      query: ({ contact_id, data }) => ({
        url: `/organizations/contacts/${contact_id}/candidate-link`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'OrgContacts', id: args.contact_id }],
    }),

    removeContact: builder.mutation<APIResponse, RemoveContactProps>({
      query: (data) => ({
        url: `/organizations/contacts/${data.contact_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: ['OrgContacts'],
    }),

    getContactCompanyLinks: builder.query<PaginatedResponse<IContactCompanyLink>, { contact_id: string, params: PageOptions & { search_query?: string } }>({
      query: ({ contact_id, params }) => ({
        url: `/organizations/contacts/${contact_id}/company-links`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'ContactCompanyLink', id: args.contact_id }],
    }),

    addContactCompanyLink: builder.mutation<APIResponse, { contact_id: string, company_profiles: string[] }>({
      query: ({ contact_id, company_profiles }) => ({
        url: `/organizations/contacts/${contact_id}/company-links`,
        method: HTTPRequestType.POST,
        data: {
          company_profiles
        },
      }),
      invalidatesTags: (_res, _err, args) => [
        { type: 'ContactCompanyLink', id: args.contact_id },
        { type: 'OrgContacts', id: args.contact_id }
      ],
    }),

    removeContactCompanyLink: builder.mutation<APIResponse, { contact_id: string, link_id: string }>({
      query: ({ contact_id, link_id }) => ({
        url: `/organizations/contacts/${contact_id}/company-links/${link_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => [
        { type: 'ContactCompanyLink', id: args.contact_id },
        { type: 'OrgContacts', id: args.contact_id }
      ],
    }),


    getContactLinks: builder.query<PaginatedResponse<IContactLink>, { contact_id: string, params: PageOptions & { search_query?: string } }>({
      query: ({ contact_id, params }) => ({
        url: `/organizations/contacts/${contact_id}/contact-links`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'ContactLink', id: args.contact_id }],
    }),

    addContactLink: builder.mutation<APIResponse, { data: AddContactLinkProps }>({
      query: ({ data }) => ({
        url: `/organizations/contact-links`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: (_res, _err, args) => {
        switch (args.data.entity_type) {
          case ContactLinkEntityType.COMPANY_PROFILE:
            return [{ type: 'CompanyContactLink', id: args.data.entity_id }];
          case ContactLinkEntityType.MEETING:
            return [{ type: 'MeetingContactLink', id: args.data.entity_id }];
          case ContactLinkEntityType.CONTACT:
            return [{ type: 'ContactLink', id: args.data.entity_id }];
          case ContactLinkEntityType.DEAL:
            return [{ type: 'DealContactLinks', id: args.data.entity_id }];
          default:
            return [];
        }
      }
    }),

    removeContactLink: builder.mutation<APIResponse, { entity_id: string, entity_type: ContactLinkEntityType, link_id: string }>({
      query: ({ link_id, entity_id, entity_type }) => ({
        url: `/organizations/contact-links/${link_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => {
        switch (args.entity_type) {
          case ContactLinkEntityType.COMPANY_PROFILE:
            return [{ type: 'CompanyContactLink', id: args.entity_id }];
          case ContactLinkEntityType.MEETING:
            return [{ type: 'MeetingContactLink', id: args.entity_id }];
          case ContactLinkEntityType.CONTACT:
            return [{ type: 'ContactLink', id: args.entity_id }];
          case ContactLinkEntityType.DEAL:
            return [{ type: 'DealContactLinks', id: args.entity_id }];
          default:
            return [];
        }
      }
    }),

    // documents
    getContactDocuments: builder.query<PaginatedResponse<ContactDocument>, { contact_id: string, params: PageOptions }>({
      query: ({ contact_id, params }) => ({
        url: `/organizations/contacts/${contact_id}/documents`,
        params,
      }),
      providesTags: (_res, _err, args) => [{ type: 'ContactDocuments', id: args.contact_id }],
    }),
    addContactDocument: builder.mutation<APIResponse, { contact_id: string, data: ICreateContactDocumentDto }>({
      query: ({ contact_id, data }) => ({
        url: `/organizations/contacts/${contact_id}/documents`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'ContactDocuments', id: args.contact_id }],
    }),
    removeContactDocument: builder.mutation<APIResponse, { contact_id: string, document_id: string }>({
      query: ({ contact_id, document_id }) => ({
        url: `/organizations/contacts/${contact_id}/documents/${document_id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (_res, _err, args) => [{ type: 'ContactDocuments', id: args.contact_id }],
    }),

    getContactActivities: builder.query<PaginatedResponse<Activity>, { contact_id: string; page: number; perPage: number }>({
      query: ({ contact_id, page, perPage }) => ({
        url: `/organizations/contacts/${contact_id}/activities`,
        params: { page, per_page: perPage },
      }),
    }),
  })
});

export const {

  useGetOrganizationContactsQuery,
  useGetOrganizationContactsLeanQuery,
  useGetContactByIdQuery,
  useAddContactMutation,
  useUpdateContactMutation,
  useUpdateContactAccountOwnerMutation,
  useRemoveContactMutation,
  useUpdateContactCandidateLinkMutation,
  useGetContactLinksQuery,
  useBulkContactImportMutation,

  useGetContactCompanyLinksQuery,
  useAddContactCompanyLinkMutation,
  useRemoveContactCompanyLinkMutation,

  useAddContactLinkMutation,
  useRemoveContactLinkMutation,

  useGetContactDocumentsQuery,
  useAddContactDocumentMutation,
  useRemoveContactDocumentMutation,

  useGetContactActivitiesQuery,

} = contactsApi;
